import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { MoreMenuVerticalOutlinedIcon, StarOutlinedIcon, TrashOutlinedIcon } from '@getgo/chameleon-icons/react';
import {
  ChipV2,
  IconButton,
  Menu,
  MenuItem,
  PopoverV2,
  TextBadge,
  Typography,
} from '@getgo/chameleon-web-react-wrapper';
import { AsyncThunk } from '@reduxjs/toolkit';

import getEnvironment from 'config/getEnvironment';
import { useAppDispatch } from 'hooks';
import { showErrorSnack } from 'modules/error';
import { postPaymentMethods, postPaymentMethodsDelete, postPaymentMethodsSetDefault } from 'modules/payment-methods';
import Track, { PayNowEventNames } from 'modules/tracking';
import { PaymentMethodDeletePayload, PaymentMethodSetDefaultPayload, PaymentMethodsState } from 'types/payment-method';
import { Thunk } from 'types/root';
import { ENVIRONMENTS, PARTNER_TYPES, SEARCH_PARAMS } from 'utils/constants';
import st from 'utils/shared-translations';

import './default-delete-ctas.css';

interface DefaultDeleteCTAsProps {
  isDefault: boolean;
  isDeletable: boolean;
  paymentMethodKey: string;
  paymentType: string;
  isAdyen?: boolean;
}

const DefaultDeleteCTAs: FC<DefaultDeleteCTAsProps> = ({
  isDefault,
  isDeletable,
  paymentMethodKey,
  paymentType,
  isAdyen,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const [isAPILoading, setAPILoading] = useState(false);

  const partnerType = localStorage.getItem(SEARCH_PARAMS.partnerType);

  /**
   * This method handles both API call (delete and set as default)
   * based on the passed action.
   */
  const handleAPICall = (
    action: AsyncThunk<PaymentMethodsState, PaymentMethodSetDefaultPayload | PaymentMethodDeletePayload, Thunk>,
    trackingEvent: string,
  ) => {
    const payload = { paymentMethodKey };
    Track(trackingEvent, { key: paymentMethodKey.slice(-5), paymentMethod: paymentType });

    setAPILoading(true);
    dispatch(action(payload))
      .unwrap()
      .then(() => {
        setAPILoading(false);
        dispatch(postPaymentMethods())
          .unwrap()
          .catch(() => dispatch(showErrorSnack(st['alert.error.general.refreshtryagain'])));
      })
      .catch(() => {
        setAPILoading(false);
        dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
      });
  };

  const showMenu = isDeletable || !isDefault;

  const currentEnv = getEnvironment();
  const isDevEnv = currentEnv === ENVIRONMENTS.development || currentEnv === ENVIRONMENTS.local;

  return (
    <div className="default-delete-ctas">
      {isAdyen && isDevEnv && <TextBadge variant="success">Adyen</TextBadge>}
      {isDefault && (
        <ChipV2 aria-label="primary bank" className="default-delete-ctas__primary" size="small">
          <Typography
            tag="span"
            className="default-delete-ctas__chip-text"
            variant="caption-medium-strong"
            color="type-color-inverted"
          >
            {intl.formatMessage(st['payment.methods.default'])}
          </Typography>
        </ChipV2>
      )}
      {showMenu && (
        <>
          <IconButton disabled={isAPILoading} labelId="Menu options" id="default-delete-ctas">
            <MoreMenuVerticalOutlinedIcon />
          </IconButton>
          <PopoverV2 menu triggerId="default-delete-ctas">
            <Menu labelId="default-delete-ctas__menu">
              {!isDefault && (
                <MenuItem
                  disabled={isAPILoading || partnerType?.toUpperCase() === PARTNER_TYPES.parent}
                  onClick={() => handleAPICall(postPaymentMethodsSetDefault, PayNowEventNames.setDefault)}
                  leadingIcon={<StarOutlinedIcon />}
                  aria-label="Set as default"
                >
                  <Typography tag="span" className="default-delete-ctas__set-default" variant="caption-medium">
                    {intl.formatMessage(st['payment.methods.cta.setdefault'])}
                  </Typography>
                </MenuItem>
              )}
              {isDeletable && (
                <MenuItem
                  disabled={isAPILoading || partnerType === PARTNER_TYPES.parent}
                  onClick={() => handleAPICall(postPaymentMethodsDelete, PayNowEventNames.delete)}
                  leadingIcon={<TrashOutlinedIcon />}
                  aria-label="Delete payment method"
                >
                  <Typography tag="span" variant="caption-medium">
                    {intl.formatMessage(st['payment.methods.cta.remove'])}
                  </Typography>
                </MenuItem>
              )}
            </Menu>
          </PopoverV2>
        </>
      )}
    </div>
  );
};

export default DefaultDeleteCTAs;
